import type { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { Errors, UnauthorizedScopesError } from '#definitions/errors';
import NoSitesErrorPage from './NoSitesErrorPage';
import NoAccessErrorPage from './NoAccessErrorPage';
import GenericErrorPage from './GenericErrorPage';
import { Box, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

const contentStyle = xcss({
  paddingTop: 'space.400',
  margin: '0 auto',
  maxWidth: '550px',
});

const ErrorPage: FunctionComponent<{ error: Errors | any }> = ({ error }) => {
  const { formatMessage, formatList } = useIntl();
  console.error('Caught error', error);

  if (!isError(error)) {
    console.error('This error cannot be handled by the error page', error);
    throw error;
  }

  const determineErrorPage = () => {
    switch (error.error) {
      case 'no-sites':
        return <NoSitesErrorPage {...error} />;
      case 'no-access':
        return <NoAccessErrorPage {...error} />;
      case 'unauthorized-scopes':
        return (
          <GenericErrorPage
            {...error}
            description={formatMessage(
              errorMessages.unauthorizedScopesDescription,
              {
                scopes: formatList(error.scopes, { type: 'conjunction' }),
                products: formatList(error.products, { type: 'conjunction' }),
                link: <ConsoleLink error={error} />,
              },
            )}
          />
        );
      case 'no-scopes':
        return (
          <GenericErrorPage
            {...error}
            description={formatMessage(errorMessages.noScopesDescription)}
          />
        );
      case 'agent-unavailable':
        return (
          <GenericErrorPage
            {...error}
            description={formatMessage(
              errorMessages.agentUnavailableDescription,
              {
                link: (text) => (
                  <Link href={RAISE_SUPPORT_TICKET_LINK}>{text}</Link>
                ),
              },
            )}
          />
        );
      case 'unexpected':
        return <GenericErrorPage {...error} />;
      default:
        console.error('Unexpected error type', error);
        throw error;
    }
  };

  return <Box xcss={contentStyle}>{determineErrorPage()}</Box>;
};

function isError(error: any): error is Errors {
  // If error is not null/undefined, is an object, and has an "error" field string
  return error && typeof error === 'object' && typeof error.error === 'string';
}

export const imageContainerStyling = {
  width: '100%',
  maxHeight: '300px',
};

const errorMessages = defineMessages({
  agentUnavailableDescription: {
    id: 'error.agentMcpMetadata.description',
    defaultMessage:
      'There was an issue with accessing this custom agent. <link>Please contact support</link> if this continues.',
    description:
      'The Rovo agent the app wants to connect to could not be loaded. The link opens the Atlassian support contact page.',
  },
  unauthorizedScopesDescription: {
    id: 'error.unauthorizedScopes.description',
    defaultMessage:
      'This app has requested {products} scopes that have not been added to the app. Configure the app at {link} and add the following scopes to the app: {scopes}.',
    description:
      'The app is requesting {scopes} for the {products} listed that have not been added to the app by the developer.',
  },
  noScopesDescription: {
    id: 'error.noScopes.description',
    defaultMessage:
      'This app has not requested any supported Atlassian scopes. Check the authorization URL for your app and ensure that it includes valid scopes.',
    description:
      "This app has either requested no scopes or all the scopes they have requested aren't valid scopes. One potential cause is the authorization URL used by the app doesn't have valid scopes in the scopes field.",
  },
});

export const RAISE_SUPPORT_TICKET_LINK =
  'https://support.atlassian.com/contact';

const ConsoleLink: FunctionComponent<{ error: UnauthorizedScopesError }> = ({
  error,
}) => {
  const link =
    error.products.length === 1 &&
    'trello' === error.products.at(0)?.toLowerCase()
      ? getTrelloLink()
      : DAC_LINK;
  return <Link href={link}>{link}</Link>;
};

export const DAC_LINK = 'https://developer.atlassian.com/apps';

const getTrelloLink = () => {
  switch (process.env.ENV_TYPE) {
    case 'stg':
      return 'https://trellis.coffee/apps/admin';
    default:
      return 'https://trello.com/apps/admin';
  }
};

export default ErrorPage;
